import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Facilities } from "../../api/Facilities";
import { Facility } from "../../types/Structure";
import { useToasts } from "../../contexts/toasts";
import { Breadcrumb, Button, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { TailSpin } from "react-loader-spinner";
import {
  Form,
  DatePicker,
  Checkbox,
  Select,
  ConfigProvider,
  Input,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import itIT from "antd/locale/it_IT";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { Bookings } from "../../api/Booking";
import {
  BookingScope,
  BookingStatus,
  BookingType,
} from "../../enums/BookingScope";
import { Structures } from "../../api/Structure";
import { Option } from "../../models/generics";
import { MatchType } from "../../types/Result";
import BookingColorPicker from "./BookingColorPicker";
import { CustomTypeInput } from "./BookingTypeSelect";
import {
  BOOKING_TYPE_OPTIONS,
  CUSTOM_BOOKING_TYPE,
  CUSTOM_TYPE_BASE_BOOKING_TYPE,
  rememberCustomType,
} from "../../utils/customBookingType";
import TextArea from "antd/es/input/TextArea";

dayjs.locale("it");

const { RangePicker } = DatePicker;

const weekDays = [
  { label: "Lunedì", value: 1 },
  { label: "Martedì", value: 2 },
  { label: "Mercoledì", value: 3 },
  { label: "Giovedì", value: 4 },
  { label: "Venerdì", value: 5 },
  { label: "Sabato", value: 6 },
  { label: "Domenica", value: 0 },
];

export default function BulkBooking() {
  const { facilityId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const [facility, setFacility] = useState<Facility>();
  // Uno dei tipi standard oppure CUSTOM_BOOKING_TYPE (tipologia personalizzata).
  const [bookingType, setBookingType] = useState<
    BookingType | typeof CUSTOM_BOOKING_TYPE
  >();
  const [matchType, setMatchType] = useState<MatchType>();
  const [scope, setScope] = useState<BookingScope>();
  const [coachesList, setCoachesList] = useState<Option[]>();
  const [form] = Form.useForm();
  const [usersList, setUsersList] = useState<Option[]>();
  const [submitLoading, setSubmitLoading] = useState(false);

  const range = Form.useWatch("range", form);
  const days = Form.useWatch("days", form);
  const slot = Form.useWatch("slot", form);
  const coachId = Form.useWatch("coachId", form);
  const player1Team1Id = Form.useWatch("player1Team1Id", form);
  const inviteLevel = Form.useWatch("inviteLevel", form);
  const customType = Form.useWatch("customType", form);
  const isCustomType = bookingType === CUSTOM_BOOKING_TYPE;

  const initFavorites = useCallback(() => {
    Structures.getHostFavorites().then(
      ({ data }) => {
        setUsersList(
          data.list.map((user) => ({
            label: `${user.user.name} ${user.user.familyName}`,
            value: user.userId,
          }))
        );
      },
      () => {
        showToast(
          "players-error",
          "Impossibile ottenere la lista dei giocatori",
          "",
          "danger"
        );
        setUsersList([]);
      }
    );
  }, [showToast]);

  const initFacility = useCallback(() => {
    Facilities.getFacilityById(facilityId!).then(
      ({ data }) => setFacility(data.facility),
      () => {
        showToast(
          "facility-error",
          "Impossibile ottenere i dati della struttura",
          "",
          "danger"
        );
        navigate(-1);
      }
    );
  }, [showToast, facilityId, navigate]);

  const initCoaches = useCallback(() => {
    Structures.getStructureCoaches().then(
      ({ data }) => {
        setCoachesList(
          data.list.map((coach) => ({
            label: `${coach.user.name} ${coach.user.familyName}`,
            value: coach.userId,
          }))
        );
      },
      () => {
        showToast(
          "coaches-error",
          "Impossibile ottenere la lista dei maestri",
          "",
          "danger"
        );
        setCoachesList([]);
      }
    );
  }, [showToast]);

  useEffect(() => {
    if (facility && [1, 2, 7].includes(facility.sportId)) {
      initCoaches();
      initFavorites();
    }
  }, [facility, initCoaches, initFavorites]);

  useEffect(() => {
    if (facilityId) initFacility();
  }, [facilityId, initFacility]);

  const handleSubmit = useCallback(
    (values: any) => {
      setSubmitLoading(true);
      const [start, end] = values.range;
      // Tipologia personalizzata (es. "Manutenzione"): etichetta libera su una
      // prenotazione sempre privata; il tipo standard resta quello di base.
      const isCustom = values.bookingType === CUSTOM_BOOKING_TYPE;
      const customTypeLabel = isCustom
        ? (values.customType || "").trim()
        : null;
      rememberCustomType(customTypeLabel);
      const payload: any = {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        daysOfWeek: values.days,
        timeSlot: values.slot,
        bookingType: isCustom
          ? CUSTOM_TYPE_BASE_BOOKING_TYPE
          : values.bookingType,
        customType: customTypeLabel,
        coachId: isCustom ? undefined : values.coachId,
        facilityId: facility!.id,
        status: BookingStatus.Pending,
        scope: isCustom ? BookingScope.Private : values.scope,
        sportId: facility!.sportId,
        inviteLevel: values.inviteLevel || 5,
        createdByHost: true,
        structureId: facility!.structureId,
        notes: values.notes || "Creata massivamente",
        color: values.color || null,
      };
      if (
        values.player1Team1Id ||
        values.player1Team2Id ||
        values.player2Team1Id ||
        values.player2Team2Id
      ) {
        payload.match = {
          player1Team1Id: values.player1Team1Id,
          player2Team1Id: values.player2Team1Id,
          player1Team2Id: values.player1Team2Id,
          player2Team2Id: values.player2Team2Id,
          matchType,
          score: [],
        };
      }
      Bookings.createBulkBookings(payload).then(
        ({ data }) => {
          setSubmitLoading(false);
          showToast(
            "bulk-booking-create-success",
            "Prenotazioni salvate correttamente",
            "",
            "success"
          );
          navigate(-1);
        },
        () => {
          showToast(
            "bulk-booking-error",
            "Impossibile salvare le prenotazioni ricorrenti",
            "",
            "danger"
          );
          setSubmitLoading(false);
        }
      );
    },
    [facility, matchType, navigate, showToast]
  );

  const isSubmitButtonDisabled = useMemo(() => {
    console.log(inviteLevel);
    if (!facility) return true;
    if (isCustomType) return !(range && days && slot && customType?.trim());
    if (![1, 2, 7].includes(facility.sportId)) {
      return !(range && days && slot && bookingType);
    } else {
      if (bookingType === BookingType.Training && (!coachId || !player1Team1Id))
        return true;
      if (scope === BookingScope.Open && inviteLevel === undefined) return true;
      return !(range && days && slot && bookingType && scope);
    }
  }, [
    facility,
    range,
    days,
    slot,
    bookingType,
    coachId,
    player1Team1Id,
    inviteLevel,
    scope,
    isCustomType,
    customType,
  ]);

  const handleBookingTypeChange = useCallback(
    (type: BookingType | typeof CUSTOM_BOOKING_TYPE) => {
      setBookingType(type);
    },
    []
  );

  const handleMatchTypeChange = useCallback((type: MatchType) => {
    setMatchType(type);
  }, []);

  const handleScopeChange = useCallback((scope: BookingScope) => {
    setScope(scope);
  }, []);

  return (
    <Container className="mt-5">
      <Helmet>
        <title>
          {facility?.name + " - " || ""}Gestisci prenotazioni ricorrenti
        </title>
      </Helmet>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/prenotazioni" }}>
          Prenotazioni
        </Breadcrumb.Item>
        {facility ? (
          <>
            <Breadcrumb.Item active>{facility.name}</Breadcrumb.Item>
          </>
        ) : (
          <Breadcrumb.Item active>
            <TailSpin color="#f25d0b" width={20} height={20} />
          </Breadcrumb.Item>
        )}
      </Breadcrumb>

      <ConfigProvider locale={itIT}>
        <Form form={form} layout="horizontal" onFinish={handleSubmit}>
          <Row className="gx-0 d-inline-flex flex-row w-100 justify-content-around border-bottom p-2 px-3">
            <Col>
              <h5 className="my-2">
                {facility?.name} - Prenotazione Ricorrente
              </h5>
            </Col>
            <Col className="text-end">
              <Button
                size="sm"
                disabled={submitLoading || isSubmitButtonDisabled}
                variant="success"
                type="submit"
              >
                {submitLoading ? (
                  <TailSpin width={20} height={20} />
                ) : (
                  <>
                    <FontAwesomeIcon className="me-2" icon={faSave} />
                    Salva
                  </>
                )}
              </Button>
            </Col>
          </Row>
          <Form.Item
            className="mt-3"
            name="range"
            label="Seleziona data di inizio e di fine"
            rules={[
              { required: true, message: "Seleziona un intervallo di date" },
            ]}
          >
            <RangePicker
              format="DD/MM/YYYY"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
              placeholder={["Data di inizio", "Data di fine"]}
            />
          </Form.Item>
          <Form.Item
            name="days"
            label="Giorni della settimana"
            rules={[{ required: true, message: "Seleziona almeno un giorno" }]}
          >
            <Checkbox.Group
              options={(facility?.timeSlots || [])
                .filter((d) => d.enabled) // solo giorni abilitati
                .map((d) => ({
                  label: weekDays.find((w) => w.value === d.dayOfWeek)?.label,
                  value: d.dayOfWeek,
                }))}
            />
          </Form.Item>

          <Form.Item
            name="slot"
            label="Orario"
            rules={[{ required: true, message: "Seleziona l'orario" }]}
          >
            <Select
              style={{ width: 200 }}
              options={
                days && facility?.timeSlots
                  ? facility.timeSlots
                      .filter((d) => days.includes(d.dayOfWeek) && d.enabled)
                      .flatMap((d) =>
                        d.slots.map((s) => ({
                          label: s.time,
                          value: s.time,
                        }))
                      )
                  : []
              }
              placeholder="Scegli un orario"
            />
          </Form.Item>

          {/* Le tipologie personalizzate sono sempre private */}
          {[1, 2, 7].includes(facility?.sportId || 1) && !isCustomType && (
            <>
              <Form.Item name="scope" label="Visibilità della partita">
                <Select
                  style={{ width: 200 }}
                  options={[
                    { label: "Privata", value: BookingScope.Private },
                    { label: "Pubblica", value: BookingScope.Open },
                  ]}
                  className="p-0"
                  placeholder="Scegli la visiblità della partita"
                  onSelect={handleScopeChange}
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </Form.Item>
              {scope === BookingScope.Open && (
                <Form.Item name="inviteLevel" label="Livello Minimo">
                  <Input
                    style={{ width: 200 }}
                    type="number"
                    min={4.5}
                    max={10.5}
                    step={0.5}
                    onChange={(e) => {
                      let value = parseFloat(e.target.value);
                      if (value < 4.5) value = 4.5;
                      if (value > 10) value = 10;
                      form.setFieldValue("inviteLevel", value);
                    }}
                  />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item
            name="bookingType"
            label="Tipo di prenotazione"
            rules={[
              { required: true, message: "Seleziona il tipo di prenotaizone" },
            ]}
          >
            <Select
              style={{ width: 200 }}
              options={[
                ...BOOKING_TYPE_OPTIONS,
                { label: "Personalizzata…", value: CUSTOM_BOOKING_TYPE },
              ]}
              onChange={handleBookingTypeChange}
              placeholder="Scegli il tipo"
            />
          </Form.Item>

          {isCustomType && (
            <Form.Item
              name="customType"
              label="Nome della tipologia"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Scrivi il nome della tipologia",
                },
              ]}
            >
              <CustomTypeInput style={{ width: 200 }} autoFocus />
            </Form.Item>
          )}

          {bookingType === BookingType.Training &&
            [1, 2, 7].includes(facility?.sportId || 1) && (
              <Form.Item name="coachId" label="Scegli il maestro">
                {coachesList ? (
                  <>
                    <Select
                      style={{ width: 200 }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                      options={coachesList}
                      className="p-0"
                      placeholder="Scegli il maestro"
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </>
                ) : (
                  <div className="d-flex py-3">
                    <TailSpin
                      height={20}
                      width={20}
                      color="#f25d0b"
                      radius="1"
                    />
                  </div>
                )}
              </Form.Item>
            )}

          {[1, 2, 7].includes(facility?.sportId || 1) && (
            <>
              {facility?.sportId !== 1 && (
                <Form.Item
                  name="matchType"
                  label="Tipo di Partita (Singolare o doppio)"
                  rules={[
                    {
                      required: !isCustomType,
                      message: "Seleziona il tipo di partita",
                    },
                  ]}
                >
                  <Select
                    style={{ width: 200 }}
                    options={[
                      { label: "Singolare", value: MatchType.Single },
                      { label: "Doppio", value: MatchType.Double },
                    ]}
                    className="p-0"
                    placeholder="Scegli il tipo di partita"
                    onSelect={handleMatchTypeChange}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                </Form.Item>
              )}
              <Row>
                <div className="d-flex flex-column gap-3">
                  <h6>
                    {facility?.sportId === 1 || matchType === MatchType.Double
                      ? "Coppia"
                      : "Giocatore"}{" "}
                    1:
                  </h6>
                  {!usersList ? (
                    <div className="d-flex py-3">
                      <TailSpin
                        height={20}
                        width={20}
                        color="#f25d0b"
                        radius="1"
                      />
                    </div>
                  ) : (
                    <Form.Item
                      name="player1Team1Id"
                      label="Giocatore 1"
                      rules={[
                        {
                          required: bookingType === BookingType.Training,
                          message: "Seleziona il Giocatore 1",
                        },
                      ]}
                    >
                      <Select
                        style={{ width: 200 }}
                        showSearch
                        options={usersList}
                        className="p-0"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        allowClear
                        placeholder="Giocatore 1"
                        getPopupContainer={(trigger) => trigger.parentNode}
                      />
                    </Form.Item>
                  )}
                  {(facility?.sportId === 1 ||
                    matchType === MatchType.Double) && (
                    <>
                      {!usersList ? (
                        <div className="d-flex py-3">
                          <TailSpin
                            height={20}
                            width={20}
                            color="#f25d0b"
                            radius="1"
                          />
                        </div>
                      ) : (
                        <Form.Item name="player2Team1Id" label="Giocatore 2">
                          <Select
                            style={{ width: 200 }}
                            showSearch
                            options={usersList}
                            className="p-0"
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            allowClear
                            placeholder="Giocatore 2"
                            getPopupContainer={(trigger) => trigger.parentNode}
                          />
                        </Form.Item>
                      )}
                    </>
                  )}
                  <h6>
                    {facility?.sportId === 1 || matchType === MatchType.Double
                      ? "Coppia"
                      : "Giocatore"}{" "}
                    2:
                  </h6>
                  {!usersList ? (
                    <div className="d-flex py-3">
                      <TailSpin
                        height={20}
                        width={20}
                        color="#f25d0b"
                        radius="1"
                      />
                    </div>
                  ) : (
                    <Form.Item name="player1Team2Id" label="Giocatore 1">
                      <Select
                        style={{ width: 200 }}
                        showSearch
                        options={usersList}
                        className="p-0"
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        allowClear
                        placeholder="Giocatore 1"
                        getPopupContainer={(trigger) => trigger.parentNode}
                      />
                    </Form.Item>
                  )}
                  {(facility?.sportId === 1 ||
                    matchType === MatchType.Double) && (
                    <>
                      {!usersList ? (
                        <div className="d-flex py-3">
                          <TailSpin
                            height={20}
                            width={20}
                            color="#f25d0b"
                            radius="1"
                          />
                        </div>
                      ) : (
                        <Form.Item name="player2Team2Id" label="Giocatore 2">
                          <Select
                            style={{ width: 200 }}
                            showSearch
                            options={usersList}
                            className="p-0"
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            allowClear
                            placeholder="Giocatore 2"
                            getPopupContainer={(trigger) => trigger.parentNode}
                          />
                        </Form.Item>
                      )}
                    </>
                  )}
                </div>
              </Row>
            </>
          )}

          <Form.Item name="notes" label="Note">
            <TextArea
              style={{ width: 400 }}
              rows={4}
              size="large"
              placeholder="Note..."
            />
          </Form.Item>

          <Form.Item name="color" label="Colore nel calendario">
            <BookingColorPicker />
          </Form.Item>
        </Form>
      </ConfigProvider>
    </Container>
  );
}
